import type { ApolloClient } from '@apollo/client';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { last } from 'lodash';

import type { UIMessageWithMetadata } from './types';
import { GET_CHAT_WITH_MESSAGES, GET_CHATS_BY_FILTERS, type ChatAppType } from '../../../queries/chats';

/**
 * Normalizes a chat UUID by removing the 'new-chat-' prefix if present.
 * This ensures consistent UUID handling across the codebase.
 */
export function normalizeChatUuid(chatUuid: string): string {
  return chatUuid.replace('new-chat-', '');
}

/**
 * Builds notification text from chat data.
 * Prefers the last assistant message text, falling back to chat title.
 */
export async function buildNotificationText({
  apolloClient,
  chatUuid,
}: {
  apolloClient: ApolloClient<unknown>;
  chatUuid: string;
}): Promise<string> {
  const chatUuidWithoutPrefix = normalizeChatUuid(chatUuid);
  const chatTitleCallback = 'You have a new message';

  try {
    const { data } = await apolloClient.query({
      query: GET_CHAT_WITH_MESSAGES,
      variables: { uuid: chatUuidWithoutPrefix },
      fetchPolicy: 'network-only',
    });

    if (data?.getChat?.messages && data.getChat.messages.length > 0) {
      const lastAssistantMessage = [...data.getChat.messages].reverse().find((msg) => msg.role === 'assistant');
      const lastMessagePart = lastAssistantMessage
        ? last(lastAssistantMessage.parts as UIMessageWithMetadata['parts'])
        : undefined;
      if (lastMessagePart?.type === 'text' && typeof lastMessagePart.text === 'string') {
        return lastMessagePart.text.slice(0, 100) || chatTitleCallback;
      }
    }
  } catch (error) {
    browserLogger.debug('[Notification Helpers] Failed to fetch chat data for notification', {
      error,
      chatUuid,
    });
  }

  return chatTitleCallback;
}

/**
 * Updates the Apollo cache to reflect stream completion.
 * Sets activeStreamId to null and optionally sets hasUnreadMessages.
 */
export function updateChatsListAfterStream({
  apolloClient,
  chatUuid,
  userUuid,
  appType,
  hasUnreadMessages,
}: {
  apolloClient: ApolloClient<unknown>;
  chatUuid: string;
  userUuid: string;
  appType?: ChatAppType | null;
  hasUnreadMessages?: boolean;
}): void {
  const chatUuidWithoutPrefix = normalizeChatUuid(chatUuid);

  // Skip cache update for new chats that haven't been persisted yet
  if (chatUuid.startsWith('new-chat-')) {
    return;
  }

  try {
    const cachedData = apolloClient.readQuery({
      query: GET_CHATS_BY_FILTERS,
      variables: { userUuid, appType },
    });

    if (cachedData?.chatsList) {
      const updatedChatsList = cachedData.chatsList.map((chat) => {
        if (chat.uuid === chatUuidWithoutPrefix || chat.uuid === chatUuid) {
          return {
            ...chat,
            activeStreamId: null,
            ...(hasUnreadMessages !== undefined && { hasUnreadMessages }),
          };
        }
        return chat;
      });

      apolloClient.writeQuery({
        query: GET_CHATS_BY_FILTERS,
        variables: { userUuid, appType },
        data: { chatsList: updatedChatsList },
      });
    }
  } catch (error) {
    // Cache update is not critical - log but don't fail
    browserLogger.debug('[Notification Helpers] Could not update Apollo cache', {
      error,
      chatUuid,
    });
  }
}
